import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { formatDate, formatBytes, resolveImageUrl } from "../../lib/format";
import { downloadInstaller } from "../../lib/download";

export default function MyPlugins() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState({});

    useEffect(() => {
        api.get("/me/plugins").then(({ data }) => setItems(data || [])).finally(() => setLoading(false));
    }, []);

    const handleDownload = async (installer) => {
        setDownloading((d) => ({ ...d, [installer.id]: true }));
        const res = await downloadInstaller(installer.id, installer.file_name);
        setDownloading((d) => ({ ...d, [installer.id]: false }));
        if (res.ok) toast.success("Download started");
        else toast.error(res.error || "Could not download");
    };

    return (
        <DashboardLayout role="client">
            <div className="mb-8" data-testid="my-plugins-page">
                <div className="label-mono mb-3">/ My Plugins</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">My Plugins</h1>
                <p className="text-avx-muted mt-2">Plugins available for download (paid purchases).</p>
                {items.filter((i) => i.has_update).length > 0 && (
                    <div className="mt-5 inline-flex items-center gap-2 bg-avx-blue/15 border border-avx-blue/40 rounded-sm px-4 py-2" data-testid="updates-counter">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-avx-blue text-avx-bg text-[9px] font-mono font-bold">↑</span>
                        <span className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue">
                            {items.filter((i) => i.has_update).length} {items.filter((i) => i.has_update).length === 1 ? "update available" : "updates available"}
                        </span>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-avx-muted font-mono">Loading…</div>
            ) : items.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted">
                    You don't have any plugins available for download yet. When a purchase is marked as <strong className="text-avx-success">Paid</strong>, it will appear here.
                </div>
            ) : (
                <div className="space-y-6" data-testid="plugins-list">
                    {items.map((it) => {
                        const winInst = it.installers.filter((i) => i.os === "Windows");
                        const macInst = it.installers.filter((i) => i.os === "macOS" || i.os === "macOS Intel" || i.os === "macOS Apple Silicon");
                        const manuals = it.installers.filter((i) => i.format === "Manual" || (i.file_name && i.file_name.endsWith(".pdf")));
                        return (
                            <div key={it.product.id} className="panel-elevated overflow-hidden" data-testid={`plugin-row-${it.product.id}`}>
                                {it.has_update && (
                                    <div className="bg-avx-blue/15 border-b border-avx-blue/40 px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`update-banner-${it.product.id}`}>
                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-avx-blue text-avx-bg text-[10px] font-mono font-bold">↑</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-mono">
                                                <span className="text-avx-blue font-bold">New version available</span>
                                                <span className="text-avx-text/80"> · v{it.latest_version}</span>
                                                {it.last_downloaded_version && (
                                                    <span className="text-avx-muted"> (your last download: v{it.last_downloaded_version})</span>
                                                )}
                                            </div>
                                        </div>
                                        <a
                                            href="#downloads"
                                            onClick={(e) => { e.preventDefault(); e.currentTarget.closest('[data-testid^="plugin-row-"]').scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                                            className="text-[10px] font-mono uppercase tracking-[0.2em] text-avx-blue hover:underline"
                                        >
                                            Download new version →
                                        </a>
                                    </div>
                                )}
                                <div className="grid md:grid-cols-[180px_1fr] gap-0">
                                    {it.product.image_url ? (
                                        <img src={resolveImageUrl(it.product.image_url)} alt="" className="w-full h-full object-cover min-h-[180px] border-r border-avx-line" />
                                    ) : (
                                        <div className="bg-avx-elevated min-h-[180px] border-r border-avx-line" />
                                    )}
                                    <div className="p-6">
                                        <div className="flex flex-wrap items-center gap-3 mb-2">
                                            <h3 className="font-heading font-bold text-2xl">{it.product.name}</h3>
                                            <StatusBadge status="paid" />
                                            <span className="label-mono">v{it.product.current_version}</span>
                                        </div>
                                        <p className="text-sm text-avx-muted leading-relaxed mb-5 max-w-2xl">{it.product.short_description}</p>

                                        <div className="space-y-3">
                                            <DownloadGroup
                                                title="Windows"
                                                installers={winInst}
                                                onDownload={handleDownload}
                                                downloading={downloading}
                                            />
                                            <DownloadGroup
                                                title="macOS"
                                                installers={macInst}
                                                onDownload={handleDownload}
                                                downloading={downloading}
                                            />
                                            {manuals.length > 0 && (
                                                <DownloadGroup
                                                    title="Manual / Documents"
                                                    installers={manuals}
                                                    onDownload={handleDownload}
                                                    downloading={downloading}
                                                />
                                            )}
                                            {it.installers.length === 0 && (
                                                <div className="text-sm text-avx-muted italic">No installers have been published for this plugin yet.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </DashboardLayout>
    );
}

function DownloadGroup({ title, installers, onDownload, downloading }) {
    if (!installers || installers.length === 0) return null;
    return (
        <div className="border border-avx-line rounded-sm p-3 bg-avx-bg/40">
            <div className="label-mono mb-3">{title}</div>
            <div className="space-y-2">
                {installers.map((inst) => {
                    const archTag = inst.os === "macOS Intel" ? "Intel" : inst.os === "macOS Apple Silicon" ? "Apple Silicon" : null;
                    return (
                    <div key={inst.id} className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-sm font-mono truncate">{inst.file_name}</div>
                                {archTag && (
                                    <span className="px-1.5 py-0.5 rounded-sm border border-avx-blue/40 bg-avx-blue/10 text-avx-blue text-[9px] font-mono uppercase tracking-[0.18em]">
                                        {archTag}
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] font-mono text-avx-muted uppercase tracking-[0.18em]">
                                v{inst.version} · {inst.format} · {formatBytes(inst.file_size)} · {formatDate(inst.created_at)}
                            </div>
                            {inst.release_notes && <div className="text-xs text-avx-muted mt-1 italic">{inst.release_notes}</div>}
                        </div>
                        <button
                            onClick={() => onDownload(inst)}
                            disabled={downloading[inst.id]}
                            data-testid={`download-btn-${inst.id}`}
                            className="btn-primary px-4 py-2 text-[10px] font-mono uppercase tracking-[0.2em] rounded-sm"
                        >
                            {downloading[inst.id] ? "Downloading…" : `Download`}
                        </button>
                    </div>
                    );
                })}
            </div>
        </div>
    );
}
